import React, { useEffect, useState } from "react";
import {
  faCircleNotch,
  faGripLines,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Col, Dropdown, Row } from "react-bootstrap";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import WhiteButton from "../Common/WhiteButton";
import ColorPicker from "./ColorPicker";
import {
  renderCondition,
  WeatherAlertOperators,
  WeatherAlertConditions,
  SignificantWeatherAlertOperators,
  unitConversions,
} from "./WeatherAlertConstants";
import InputLabel from "../Builder/nodes/Logic/Inputs/InputLabel";
import MyIcon from "../Common/MyIcon";
import WeatherConditionValueSelector from "./WeatherConditionValueSelector";
import JsonCollapse from "../Builder/Components/JsonCollapse";
import HelpButton from "../HelpContent/HelpButton";

export default function WeatherAlertCondition({
  weatherAlertData,
  setWeatherAlertData,
  errors: parentErrors,
}) {
  // Alert condition state
  const newCondition = {
    ConditionDetails: [
      {
        TimeOfDay: "both",
        Condition: "",
        ConditionOperator: "=",
        ConditionValue: [],
        conditionHelper: "",
        conditionIcon: "",
        conditionUnit: "",
        conditionOptions: [],
        selectedCondition: {},
      },
    ],
    DaysAhead: [0, 1, 2, 3, 4, 5, 6],
    Description: "",
    Color: "#FF0000",
    Order: 0,
  };

  const [conditions, setConditions] = useState([newCondition]);

  const transformConditions = (conditionsArray = []) =>
    conditionsArray
      .sort((a, b) => a.Order - b.Order)
      .map((cond, index) => ({
        ID: cond.ID || 0,
        Description: cond.Description || "",
        Color: cond.Color || "#FF0000",
        Order: index,
        ConditionDetails: cond.ConditionDetails.map((cDetails) => ({
          ...cDetails,
          conditionHelper:
            WeatherAlertConditions.find((c) => c.key === cDetails.Condition)
              ?.description || "",
          conditionIcon:
            WeatherAlertConditions.find((c) => c.key === cDetails.Condition)
              ?.icon || "",
          conditionUnit:
            WeatherAlertConditions.find((c) => c.key === cDetails.Condition)
              ?.unit || "",
          conditionOptions:
            WeatherAlertConditions.find((c) => c.key === cDetails.Condition)
              ?.options || [],
          selectedCondition:
            WeatherAlertConditions.find((c) => c.key === cDetails.Condition) ||
            {},
        })),
      }));

  // Error state
  const [errors, setErrors] = useState({});

  // Predefined options for dropdowns
  const conditionNames = WeatherAlertConditions;

  const operators = WeatherAlertOperators;

  const handleConditionChange = (field, value, index) => {
    if (typeof index !== "undefined") {
      conditions[index][field] = value;
      setConditions([...conditions]);
    }
  };
  const handleConditionDetailChange = (
    detailIndex,
    field,
    value,
    conditionIndex
  ) => {
    if (typeof conditionIndex === "number") {
      // Editing existing condition in the conditions array
      setConditions((prevConditions) => {
        const updatedConditions = [...prevConditions];

        // Update helper info when condition changes
        if (field === "Condition") {
          const selectedCondition =
            WeatherAlertConditions.find((c) => c.key === value) || {};

          // Update the nested condition detail
          updatedConditions[conditionIndex].ConditionDetails[detailIndex] = {
            ...updatedConditions[conditionIndex].ConditionDetails[detailIndex],
            [field]: value,
            conditionHelper: selectedCondition.description || "",
            conditionIcon: selectedCondition.icon || "",
            conditionUnit: selectedCondition.unit || "",
            Unit: selectedCondition.unit || "",
            conditionOptions: selectedCondition.options || [],
            selectedCondition,
          };
        } else {
          // For other fields, just update that field
          updatedConditions[conditionIndex].ConditionDetails[detailIndex] = {
            ...updatedConditions[conditionIndex].ConditionDetails[detailIndex],
            [field]: value,
          };
        }

        return updatedConditions;
      });
    }
  };

  const handleRemoveCondition = (index) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const addCondition = (e) => {
    e.preventDefault();
    if (validateCondition()) {
      setConditions((prev) => [...prev, newCondition]);
      setErrors({ ...errors, condition: "" });
    }
  };

  const addConditionDetail = (conditionIndex) => {
    if (typeof conditionIndex === "number") {
      // Editing an existing condition within the conditions array
      setConditions((prevConditions) => {
        const updatedConditions = [...prevConditions];

        // Add a new condition detail to the specified condition
        updatedConditions[conditionIndex].ConditionDetails = [
          ...updatedConditions[conditionIndex].ConditionDetails,
          {
            TimeOfDay: "both",
            Condition: "",
            ConditionOperator: "=",
            ConditionValue: [0],
            conditionHelper: "",
            conditionIcon: "",
            conditionUnit: "",
            conditionOptions: [],
            selectedCondition: {},
          },
        ];

        return updatedConditions;
      });
    }
  };

  const removeConditionDetail = (indexToRemove, conditionIndex) => {
    if (typeof conditionIndex === "number") {
      // Removing from an existing condition in the conditions array
      setConditions((prevConditions) => {
        const updatedConditions = [...prevConditions];

        // Remove the condition detail at the specified indices
        updatedConditions[conditionIndex].ConditionDetails = updatedConditions[
          conditionIndex
        ].ConditionDetails.filter((_, index) => index !== indexToRemove);

        return updatedConditions;
      });
    }
  };

  const validateCondition = () =>
    // if (newCondition.ConditionDetails.length < 0) {
    //   setErrors({ ...errors, condition: 'All condition fields are required' });
    //   return false;
    // }
    true;
  // Add these validation checks

  const isConditionValid = Boolean(
    newCondition.Description.trim() &&
      newCondition.ConditionDetails.length > 0 &&
      newCondition.ConditionDetails.every(
        (detail) =>
          detail.TimeOfDay &&
          detail.Condition &&
          Array.isArray(detail.ConditionValue) &&
          detail.ConditionValue.length > 0 &&
          detail.ConditionValue.every(
            (x) => x !== null && x !== undefined && x !== ""
          ) &&
          detail.ConditionOperator
      )
  );

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const reordered = Array.from(conditions);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);

    const updated = reordered.map((cond, index) => ({
      ...cond,
      Order: index,
    }));
    setConditions(updated);
  };

  useEffect(() => {
    if (!setWeatherAlertData) return;
    setWeatherAlertData({
      ...weatherAlertData,
      Conditions: conditions,
    });
  }, [conditions]);

  useEffect(() => {
    if (!weatherAlertData) return;
    setConditions(transformConditions(weatherAlertData.Conditions || []));
  }, []);

  return (
    <div className="">
      <Row>
        <Col>
          <div className="">
            <div className="my-3">
              <div className="col-12">
                <h4>
                  Alert Conditions
                  <HelpButton helpKey="weather-alert-conditions" />
                </h4>
                {errors.condition && (
                  <div className="text-danger">{errors.condition}</div>
                )}
              </div>
              {conditions.length > 0 && (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="conditionsList">
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.droppableProps}>
                        {conditions.map((condition, index) => (
                          <Draggable
                            key={index.toString()}
                            draggableId={`cond-${index}`}
                            index={index}
                          >
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                className="col-12 p-3 border rounded mb-3 position-relative bg-light"
                                style={{
                                  ...provided.draggableProps.style,
                                  marginBottom: "8px",
                                  height: "auto",
                                }}
                              >
                                <div className="d-flex">
                                  <div
                                    {...provided.dragHandleProps}
                                    style={{
                                      cursor: "grab",
                                      padding: "8px",
                                      alignSelf: "center",
                                      margin: "0 15px 0 0",
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faGripLines} />
                                  </div>
                                  <div className="flex-grow-1">
                                    {condition.ConditionDetails.length > 0 &&
                                      condition.ConditionDetails.map(
                                        (detail, detailIndex) => (
                                          <div key={detailIndex}>
                                            <div className="row">
                                              <div className="col-md-2">
                                                <Input
                                                  inputType="select"
                                                  key={detail.TimeOfDay}
                                                  label="Time Of Day"
                                                  value={[detail.TimeOfDay]}
                                                  onChange={(values) => {
                                                    handleConditionDetailChange(
                                                      detailIndex,
                                                      "TimeOfDay",
                                                      values[0],
                                                      index
                                                    );
                                                    handleConditionDetailChange(
                                                      detailIndex,
                                                      "Condition",
                                                      [],
                                                      index
                                                    );
                                                    handleConditionDetailChange(
                                                      detailIndex,
                                                      "ConditionValue",
                                                      [],
                                                      index
                                                    );
                                                  }}
                                                  options={[
                                                    {
                                                      value: "day",
                                                      label: "Day",
                                                    },
                                                    {
                                                      value: "night",
                                                      label: "Night",
                                                    },
                                                    {
                                                      value: "both",
                                                      label: "Day and Night",
                                                    },
                                                  ]}
                                                />
                                              </div>
                                              <div className="col-md-3">
                                                <Input
                                                  inputType="select"
                                                  key={detail.TimeOfDay}
                                                  label="Condition Name"
                                                  value={[detail.Condition]}
                                                  onChange={(values) => {
                                                    handleConditionDetailChange(
                                                      detailIndex,
                                                      "Condition",
                                                      values[0],
                                                      index
                                                    );
                                                    handleConditionDetailChange(
                                                      detailIndex,
                                                      "ConditionValue",
                                                      [],
                                                      index
                                                    );

                                                    if (
                                                      detail.selectedCondition
                                                        .windDirections
                                                    ) {
                                                      handleConditionDetailChange(
                                                        detailIndex,
                                                        "ConditionOperator",
                                                        "=",
                                                        index
                                                      );
                                                    }
                                                  }}
                                                  formatOptionLabel={(o) => {
                                                    const icon =
                                                      conditionNames.find(
                                                        (a) =>
                                                          a.label === o.label
                                                      )?.icon;
                                                    return (
                                                      <div className="d-flex align-items-center gap-2">
                                                        {icon && (
                                                          <MyIcon
                                                            width="16"
                                                            height="16"
                                                            Icon={icon}
                                                            className="text-purple bg-transparent"
                                                          />
                                                        )}
                                                        {o.label}
                                                      </div>
                                                    );
                                                  }}
                                                  options={conditionNames
                                                    .filter((c) => {
                                                      const matchesTimeOfDay =
                                                        detail.TimeOfDay ==
                                                        "day"
                                                          ? c.isDay === true
                                                          : c.isDay === false;

                                                      return matchesTimeOfDay;
                                                    })
                                                    .map((c) => ({
                                                      value: c.key,
                                                      label: c.label,
                                                    }))}
                                                />
                                              </div>

                                              <div
                                                className={`col-md-2 ${
                                                  detail.selectedCondition
                                                    .windDirections
                                                    ? "d-none"
                                                    : ""
                                                }`}
                                              >
                                                <Input
                                                  key={detail.Condition}
                                                  inputType="select"
                                                  label="Condition Operator"
                                                  value={[
                                                    detail.ConditionOperator,
                                                  ]}
                                                  onChange={(values) => {
                                                    if (values[0] == "=") {
                                                      handleConditionDetailChange(
                                                        detailIndex,
                                                        "ConditionValue",
                                                        [],
                                                        index
                                                      );
                                                    }
                                                    handleConditionDetailChange(
                                                      detailIndex,
                                                      "ConditionOperator",
                                                      values[0],
                                                      index
                                                    );
                                                  }}
                                                  options={(detail.Condition ===
                                                    "daySignificantWeatherCode" ||
                                                  detail.Condition ===
                                                    "nightSignificantWeatherCode"
                                                    ? SignificantWeatherAlertOperators
                                                    : operators
                                                  )
                                                    .filter((op) => {
                                                      if (
                                                        op.symbol === "between"
                                                      ) {
                                                        return false;
                                                      }

                                                      return true;
                                                    })
                                                    .map((op) => {
                                                      if (
                                                        op.symbol == "between"
                                                      ) {
                                                        return {
                                                          value: op.symbol,
                                                          label: op.desc,
                                                        };
                                                      }
                                                      return {
                                                        value: op.symbol,
                                                        label: `${op.desc}`,
                                                      };
                                                    })}
                                                  isDisabled={
                                                    detail.selectedCondition
                                                      .windDirections
                                                  }
                                                />
                                              </div>

                                              <div className="col d-flex align-items-center">
                                                <div className="flex-grow-1">
                                                  <WeatherConditionValueSelector
                                                    detail={detail}
                                                    detailIndex={detailIndex}
                                                    handleConditionDetailChange={
                                                      handleConditionDetailChange
                                                    }
                                                    index={index}
                                                  />
                                                </div>
                                                {detail.conditionUnit &&
                                                  detail.conditionUnit !==
                                                    "Degrees from North" &&
                                                  (unitConversions[
                                                    detail.conditionUnit
                                                  ].conversions.length > 1 ? (
                                                    <Dropdown className="align-self-end ms-n2 rounded-start-0 p-0">
                                                      <Dropdown.Toggle
                                                        variant="primary"
                                                        id="dropdown-basic"
                                                        className="w-100 rounded-start-0 fw-bold fs-5 "
                                                        style={{
                                                          height: "40px",
                                                        }}
                                                      >
                                                        {detail.Unit ||
                                                          detail.conditionUnit}
                                                      </Dropdown.Toggle>
                                                      {unitConversions[
                                                        detail.conditionUnit
                                                      ] && (
                                                        <Dropdown.Menu>
                                                          {unitConversions[
                                                            detail.conditionUnit
                                                          ].conversions.map(
                                                            (unit) => (
                                                              <Dropdown.Item
                                                                key={unit.to}
                                                                href="#/action-1"
                                                                onClick={() => {
                                                                  handleConditionDetailChange(
                                                                    detailIndex,
                                                                    "Unit",
                                                                    unit.to,
                                                                    index
                                                                  );
                                                                  handleConditionDetailChange(
                                                                    detailIndex,
                                                                    "ConditionValue",
                                                                    detail.ConditionValue.map(
                                                                      (value) =>
                                                                        unit.revert(
                                                                          unitConversions[
                                                                            detail
                                                                              .conditionUnit
                                                                          ].conversions
                                                                            .find(
                                                                              (
                                                                                conversion
                                                                              ) =>
                                                                                conversion.to ===
                                                                                detail.Unit
                                                                            )
                                                                            .convert(
                                                                              parseFloat(
                                                                                value
                                                                              )
                                                                            )
                                                                        )
                                                                    ),
                                                                    index
                                                                  );
                                                                }}
                                                              >
                                                                {unit.label}
                                                              </Dropdown.Item>
                                                            )
                                                          )}
                                                        </Dropdown.Menu>
                                                      )}
                                                    </Dropdown>
                                                  ) : (
                                                    <span
                                                      className="text-white  fw-bold fs-2 bg-primary align-self-end ms-n1 rounded-start-0 input-group-text"
                                                      style={{ height: 40 }}
                                                    >
                                                      {detail.conditionUnit}
                                                    </span>
                                                  ))}
                                              </div>

                                              <div className="col-md-2">
                                                <div className="row">
                                                  <div className="col-auto pt-4 ">
                                                    <button
                                                      onClick={() =>
                                                        detailIndex > 0
                                                          ? removeConditionDetail(
                                                              detailIndex,
                                                              index
                                                            )
                                                          : addConditionDetail(
                                                              index
                                                            )
                                                      }
                                                      type="button"
                                                      className={`btn fs-5 fw-bold ${
                                                        detailIndex > 0
                                                          ? "btn-danger "
                                                          : "btn-primary fw-bold"
                                                      }`}
                                                    >
                                                      {detailIndex > 0
                                                        ? "Remove"
                                                        : "Add"}{" "}
                                                      <FontAwesomeIcon
                                                        fontWeight={800}
                                                        icon={
                                                          detailIndex > 0
                                                            ? faTrash
                                                            : faPlus
                                                        }
                                                      />
                                                    </button>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>

                                            <div className="row mb-2 mt-1">
                                              <div className={`fs-5`}>
                                                {renderCondition(
                                                  detail.selectedCondition,
                                                  detail.TimeOfDay
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        )
                                      )}
                                    <div className="row mt-3">
                                      <div className="col-10">
                                        <Input
                                          inputType="textArea"
                                          label="Condition Description"
                                          value={[condition.Description]}
                                          onChange={(values) =>
                                            handleConditionChange(
                                              "Description",
                                              values[0],
                                              index
                                            )
                                          }
                                          options={[{ value: "", label: "" }]}
                                        />
                                      </div>
                                      <div className="col-auto pt-2">
                                        <ColorPicker
                                          label="Condition Color"
                                          value={condition.Color}
                                          onColorSelect={(color) =>
                                            handleConditionChange(
                                              "Color",
                                              color,
                                              index
                                            )
                                          }
                                        />
                                      </div>
                                    </div>
                                    <div className="col-12 mt-3">
                                      <InputLabel>
                                        Days in future to look for weather
                                        alerts
                                      </InputLabel>
                                      <Input
                                        inputType="select"
                                        isMulti
                                        value={
                                          condition.DaysAhead || [
                                            0, 1, 2, 3, 4, 5, 6,
                                          ]
                                        }
                                        onChange={(values) => {
                                          if (values.length === 0) {
                                            handleConditionChange(
                                              "DaysAhead",
                                              [0, 1, 2, 3, 4, 5, 6],
                                              index
                                            );
                                            return;
                                          }
                                          handleConditionChange(
                                            "DaysAhead",
                                            values,
                                            index
                                          );
                                        }}
                                        options={[
                                          { value: 0, label: "Today" },
                                          { value: 1, label: "Tomorrow" },
                                          {
                                            value: 2,
                                            label: "Two Days Ahead",
                                          },
                                          {
                                            value: 3,
                                            label: "Three Days Ahead",
                                          },
                                          {
                                            value: 4,
                                            label: "Four Days Ahead",
                                          },
                                          {
                                            value: 5,
                                            label: "Five Days Ahead",
                                          },
                                          {
                                            value: 6,
                                            label: "Six Days Ahead",
                                          },
                                        ]}
                                      />
                                    </div>
                                    <button
                                      type="button"
                                      className="btn btn-danger m-3"
                                      style={{
                                        position: "absolute",
                                        right: 0,
                                        top: 0,
                                      }}
                                      onClick={() =>
                                        handleRemoveCondition(index)
                                      }
                                    >
                                      <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </div>
            {/* <div className=" p-3 mb-4">
              <div className="col-12 ">
                <h4>Add New Condition</h4>
              </div>

              {newCondition.ConditionDetails.length > 0 &&
                newCondition.ConditionDetails.map((detail, index) => (
                  <div key={index}>
                    <div className="row">
                      <div className="col-md-2">
                        <Input
                          inputType="select"
                          key={detail.TimeOfDay}
                          label="Time Of Day"
                          value={[detail.TimeOfDay]}
                          onChange={(values) => {
                            handleConditionDetailChange(
                              index,
                              "TimeOfDay",
                              values[0]
                            );
                            handleConditionDetailChange(index, "Condition", "");
                            handleConditionDetailChange(
                              index,
                              "COnditionValue",
                              []
                            );
                          }}
                          options={[
                            { value: "day", label: "Day" },
                            { value: "night", label: "Night" },
                            { value: "both", label: "Day and Night" },
                          ]}
                        />
                      </div>
                      <div className="col-md-3">
                        <Input
                          inputType="select"
                          key={detail.TimeOfDay}
                          label="Condition Name"
                          value={[detail.Condition]}
                          onChange={(values) => {
                            handleConditionDetailChange(
                              index,
                              "Condition",
                              values[0]
                            );
                            handleConditionDetailChange(
                              index,
                              "ConditionValue",
                              []
                            );

                            if (detail.selectedCondition.windDirections) {
                              handleConditionDetailChange(
                                index,
                                "ConditionOperator",
                                "="
                              );
                            }
                          }}
                          formatOptionLabel={(o) => {
                            const icon = conditionNames.find(
                              (a) => a.label === o.label
                            )?.icon;
                            return (
                              <div className="d-flex align-items-center gap-2">
                                {icon && (
                                  <MyIcon
                                    width="16"
                                    height="16"
                                    Icon={icon}
                                    className="text-purple bg-transparent"
                                  />
                                )}
                                {o.label}
                              </div>
                            );
                          }}
                          options={conditionNames
                            .filter((c) => {
                              const matchesTimeOfDay =
                                detail.TimeOfDay == "day"
                                  ? c.isDay === true
                                  : c.isDay === false;

                              return matchesTimeOfDay;
                            })
                            .map((c) => ({ value: c.key, label: c.label }))}
                        />
                      </div>

                      <div
                        className={`col-md-2 ${
                          detail.selectedCondition.windDirections
                            ? "d-none"
                            : ""
                        }`}
                      >
                        <Input
                          inputType="select"
                          key={detail.Condition}
                          label="Condition Operator"
                          value={[detail.ConditionOperator]}
                          onChange={(values) => {
                            if (values[0] == "=") {
                              handleConditionDetailChange(
                                index,
                                "ConditionValue",
                                []
                              );
                            }
                            handleConditionDetailChange(
                              index,
                              "ConditionOperator",
                              values[0]
                            );
                          }}
                          options={(detail.Condition ===
                            "daySignificantWeatherCode" ||
                          detail.Condition === "nightSignificantWeatherCode"
                            ? SignificantWeatherAlertOperators
                            : operators
                          )
                            .filter((op) => {
                              if (
                                (detail.Condition ===
                                  "daySignificantWeatherCode" ||
                                  detail.Condition ===
                                    "nightSignificantWeatherCode") &&
                                op.symbol === "between"
                              ) {
                                return false;
                              }

                              return true;
                            })
                            .map((op) => {
                              if (op.symbol == "between") {
                                return {
                                  value: op.symbol,
                                  label: op.desc,
                                };
                              }
                              return {
                                value: op.symbol,
                                label: `${op.desc}`,
                              };
                            })}
                          isDisabled={detail.selectedCondition.windDirections}
                        />
                      </div>

                      <div className="col-md-3 d-flex align-items-center">
                        <WeatherConditionValueSelector
                          detail={detail}
                          detailIndex={index}
                          handleConditionDetailChange={
                            handleConditionDetailChange
                          }
                        />

                        {detail.conditionUnit && (
                          <span
                            className="text-muted align-self-end ms-n1 rounded-start-0 input-group-text"
                            style={{ height: 40 }}
                          >
                            {detail.conditionUnit}
                          </span>
                        )}
                      </div>

                      <div className="col-md-2">
                        <div className="row">
                          <div className="col-auto pt-4">
                            <button
                              onClick={() =>
                                index > 0
                                  ? removeConditionDetail(index)
                                  : addConditionDetail()
                              }
                              type="button"
                              className={`btn ${
                                index > 0 ? "btn-danger" : "btn-primary"
                              }`}
                            >
                              <FontAwesomeIcon
                                icon={index > 0 ? faTrash : faPlus}
                              />
                            </button>
                          </div>
                          <div className="col-auto pt-1" hidden={index > 0}>
                            <ColorPicker
                              onColorSelect={(color) =>
                                handleConditionChange("Color", color)
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row mb-2 mt-1">
                      <div
                        className={
                          detail.selectedCondition.windDirections
                            ? "col-md-5"
                            : "col-md-7"
                        }
                      >
                        {renderCondition(
                          detail.selectedCondition,
                          detail.TimeOfDay
                        )}
                      </div>
                    </div>
                  </div>
                ))}

              <div className="col-12 mt-3">
                <Input
                  inputType="textArea"
                  label="Condition Description"
                  value={[newCondition.Description]}
                  onChange={(values) =>
                    handleConditionChange("Description", values[0])
                  }
                  options={[{ value: "", label: "" }]}
                />
              </div>

              <div className="col-12 mt-3">
                <InputLabel>
                  Days in future to look for weather alerts{" "}
                </InputLabel>
                <Input
                  inputType="select"
                  isMulti
                  value={newCondition.DaysAhead || [0, 1, 2, 3, 4, 5, 6]}
                  onChange={(values) => {
                    if (values.length === 0) {
                      handleConditionChange("DaysAhead", [0, 1, 2, 3, 4, 5, 6]);
                      return;
                    }
                    handleConditionChange("DaysAhead", values);
                  }}
                  options={[
                    { value: 0, label: "Today" },
                    { value: 1, label: "Tomorrow" },
                    { value: 2, label: "Two Days Ahead" },
                    { value: 3, label: "Three Days Ahead" },
                    { value: 4, label: "Four Days Ahead" },
                    { value: 5, label: "Five Days Ahead" },
                    { value: 6, label: "Six Days Ahead" },
                  ]}
                />
              </div>

             
            </div> */}
            {parentErrors &&
              parentErrors["Monitor.WeatherAlert.conditions"] && (
                <div className="text-danger">
                  {parentErrors["Monitor.WeatherAlert.conditions"]}
                </div>
              )}
            <div className="col-12 mt-3">
              <WhiteButton
                as="button"
                type="button"
                icon={() => (
                  <FontAwesomeIcon
                    // fontSize={"xs"}
                    icon={faPlus}
                  />
                )}
                heading="Add Condition"
                subheading="Add a new alert condition"
                onClick={addCondition}
                // disabled={!isConditionValid}
              />
            </div>
          </div>
        </Col>
      </Row>

      <JsonCollapse json={conditions} />
    </div>
  );
}
